import React, { useState } from "react";
import propTypes from "prop-types";
import Select, {
  components,
  createFilter as rsCreateFilter
} from "react-select";
import Icon from "../icons/Icon";
import classnames from "classnames";
import Label from "./Label";
import ErrorsForField from "../form/ErrorsForField";
import { useField } from "../form/Form";
import { isObject } from "util";
import { createDataAttributes } from "../../utilities";

import "./Dropdown.scss";

// React Select
// https://react-select.com

const createFilter = filter => {
  if (isObject(filter)) {
    return rsCreateFilter(filter);
  }
  return filter;
};

const DropdownIndicator = React.memo(props => (
  <components.DropdownIndicator {...props}>
    <Icon icon="fasCaretDown" />
  </components.DropdownIndicator>
));
DropdownIndicator.displayName = "DropdownIndicator";

const IndicatorSeparator = ({ innerProps }) => (
  <span className="uikit__dropdown__indicator-separator" {...innerProps} />
);

const Dropdown = props => {
  const {
    id,
    name,
    label,
    labelElement,
    options,
    placeholder,
    value,
    filter,
    multi,
    grouped,
    className,
    disabled,
    onChange,
    onBlur,
    onValidate,
    onKeyDown,
    required,
    size,
    isClearable,
    isSearchable,
    onSearch,
    hideIndicatorUntilSearch,
    hideLabel,
    noLabel,
    compactIndicator,
    fieldErrors,
    hideErrors,
    requiredError,
    dataAttrib,
    ...rest
  } = props;

  const [searchText, setSearchText] = useState("");
  useField({
    name,
    validation: { required, requiredError },
    onValidate,
    label
  });

  const classes = classnames(
    "uikit__form-item",
    "uikit__dropdown__size__" + size,
    {
      "uikit__dropdown-multi-wrapper": multi,
      "uikit__dropdown-wrapper": !multi,
      "uikit__hide-label": hideLabel,
      "uikit__dropdown__compact-indicator": compactIndicator,
      "uikit__form__hide-errors": hideErrors
    },
    className
  );

  const inputClasses = classnames("uikit__dropdown", {
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "has-value": multi ? value.length > 0 : !!value
  });

  const handleGroupedOptionData = ({ options, value }) => {
    let arr = [];
    options.forEach(o => {
      const os = o.options.filter(opt => value.includes(opt.value));
      arr = [...arr, ...os];
    });

    return arr;
  };

  // we output the values as an array of strings...
  // but the component really wants to consume the original option objects
  const selection =
    multi && !grouped
      ? options.filter(o => value.includes(o.value)) // multi really needs the options
      : grouped
      ? handleGroupedOptionData({ options, value }) // multi+grouped has the options organized differently
      : !value
      ? ""
      : typeof value === "string" && !!options.length
      ? options.find(o => o.value === value)
      : value;

  const handleChange = (value, extras) => {
    const customEvent = { ...extras, newValue: value };

    const newValue = Array.isArray(value)
      ? value.map(a => a.value)
      : value
      ? value.value
      : "";

    onChange({ event: customEvent, field: name, value: newValue });
  };

  const handleBlur = event => {
    onBlur({ event, field: name, value });
  };

  const handleKeyDown = event => {
    if (onKeyDown) {
      onKeyDown({ event, value });
    }
  };

  const handleSearchableChange = inputValue => {
    if (isSearchable && onSearch) {
      if (inputValue.length > 0) {
        onSearch(inputValue);
      }
      setSearchText(inputValue);
    }
  };

  /* hide dropdown indicator */
  const indicators =
    hideIndicatorUntilSearch && isSearchable && searchText.length === 0
      ? { DropdownIndicator: () => null, IndicatorSeparator: () => null }
      : { DropdownIndicator, IndicatorSeparator };

  // TODO: How to set first select option to be "Select One"?
  return (
    <div className={classes} {...createDataAttributes(dataAttrib)}>
      {!noLabel && (
        <Label
          htmlFor={`dropdown-input-${id}`}
          label={label}
          labelElement={labelElement}
          required={required}
        />
      )}
      <Select
        id={id}
        name={name}
        inputId={`dropdown-input-${id}`}
        options={options}
        value={selection}
        placeholder={placeholder}
        className={inputClasses}
        filterOption={createFilter(filter)}
        isMulti={multi}
        closeMenuOnSelect={!multi}
        onChange={(value, extras) => handleChange(value, extras)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        isSearchable={isSearchable}
        onInputChange={handleSearchableChange}
        classNamePrefix="uikit__dropdown"
        isClearable={isClearable && !multi}
        components={indicators}
        isDisabled={disabled}
        menuPlacement="auto"
        minMenuHeight={200}
        // eslint-disable-next-line react/jsx-no-bind
        /*
        theme={theme => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: "#002e5d",
            // option hover background color
            primary25: "#f3f3f3",
            // option on select background color
            primary50: "#f3f3f3"
          }
        })}
        */
        // TODO: Determine what all needs exposed for our usages
        {...rest}
      />
      {!hideErrors && !!fieldErrors?.length && (
        <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
      )}
    </div>
  );
};

Dropdown.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onValidate: propTypes.func.isRequired,
  onKeyDown: propTypes.func,
  required: propTypes.bool,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
    propTypes.array,
    propTypes.shape({ value: propTypes.string, label: propTypes.string })
  ]),
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  requiredError: propTypes.string,

  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,

  //component-specific

  /** Filter config object or function to use for filtering options by text input
   * Default settings:
   * - ignoreCase: true,
   * - ignoreAccents:  true,
   * - stringify: option => `${option.label} ${option.value}`,
   * - trim: true,
   * - matchFrom: "any"
   *
   * Customer filter function needs to have the signature:
   * filter = (Option, String) => Bool
   */
  onSearch: propTypes.func,
  size: propTypes.oneOf([
    "auto",
    "full",
    "tiny",
    "sm",
    "md",
    "lg",
    "25p",
    "33p",
    "50p",
    "fitToContent",
    "fill"
  ]),
  filter: propTypes.oneOfType([
    propTypes.func,
    propTypes.shape({
      ignoreCase: propTypes.bool,
      ignoreAccents: propTypes.bool,
      stringify: propTypes.func,
      trim: propTypes.bool,
      matchFrom: propTypes.oneOf(["any", "start"])
    })
  ]),
  isClearable: propTypes.bool,
  isSearchable: propTypes.bool,
  hideIndicatorUntilSearch: propTypes.bool,
  multi: propTypes.bool,
  grouped: propTypes.bool,
  options: propTypes.arrayOf(
    propTypes.oneOfType([
      propTypes.shape({
        value: propTypes.oneOfType([propTypes.string, propTypes.number]),
        label: propTypes.oneOfType([
          propTypes.string,
          propTypes.object,
          propTypes.element
        ])
      }),
      propTypes.string,
      propTypes.number
    ])
  ).isRequired,

  placeholder: propTypes.string,
  compactIndicator: propTypes.bool,
  isLoading: propTypes.bool,
  componentname: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object)
};

Dropdown.defaultProps = {
  isSearchable: false,
  isClearable: true,
  hideIndicatorUntilSearch: false,
  placeholder: "Select One...",
  size: "auto",
  hideErrors: false,
  componentname: "Dropdown"
};

export default Dropdown;
